import { graphql } from 'gatsby';

import { ClaimWithContent } from '@mewa/ui';

import { markdownToHTML } from '../../../../../libs/markdown-to-html';
import { imagePopulate } from '../../../../../libs/strapi-populates';
import {
  ComponentProps,
  PossibleContentSections,
} from '../../fragment-and-types';
import {
  ComponentBaseStrapiRest,
  ContentLivePreviewConfig,
  ImageStrapiRest,
  PossiblePreviewContentSections,
} from '../../live-preview/types';
import { populationParams as videoPopulate } from '../video/responsive-video';
import { ResponsiveVideoPreview } from '../video/types';
import { isIntroText, Sections } from './sections';
import { ClaimWithContentSection } from './types';

export const ClaimWithContentFragment = graphql`
  fragment ClaimWithContentFragment on STRAPI__COMPONENT_SHARED_CLAIM_WITH_CONTENT {
    content: Content {
      claimPrefix: ClaimPrefix
      claim: Claim
      title: Title
      showFinalDivider: ShowFinalDivider

      content: Content {
        ... on STRAPI__COMPONENT_SHARED_INTRO_TEXT {
          strapi_component
          text {
            data {
              childMarkdownRemark {
                html
              }
            }
          }
          showCTA
        }

        ... on STRAPI__COMPONENT_SHARED_TEXTBLOCK {
          strapi_component
          Content {
            data {
              childMarkdownRemark {
                html
              }
            }
          }
        }
        ... on STRAPI__COMPONENT_SHARED_VIDEO_WITH_CAPTION {
          strapi_component
          video: Video {
            ...ResponsiveVideoFragment
          }
          caption: Caption
        }

        ... on STRAPI__COMPONENT_SHARED_RESPONSIVE_IMAGE {
          id
          strapi_component
          imageAlternativeText
          imageDesktop {
            ...StrapiImageFileFragment
          }
          imageMobile {
            ...StrapiImageFileFragment
          }
        }

        ... on STRAPI__COMPONENT_SHARED_BUTTON {
          strapi_component
          title: Title
          linkUrl: LinkUrl
          piwikEventName: PiwikEventName
          variant: Variant
          position: Position
        }
      }
    }
    strapi_component
  }
`;

export const componentName = 'shared.claim-with-content';

export type ClaimWithContentData = {
  content: {
    claimPrefix: string | null;
    claim: string;
    title: string;
    showFinalDivider?: boolean | null;

    content: ClaimWithContentSection[];
  } | null;
  strapi_component: typeof componentName;
};

export type TextBlockStrapiRest = { Content: string };
export type IntroTextStrapiRest = { text: string; showCTA: boolean };
export type VideoWithCaptionStrapiRest = {
  Caption?: string | null;
  Video: ResponsiveVideoPreview;
};
export type ResponsiveImageStrapiRest = ImageStrapiRest;

export type ClaimContent = (ComponentBaseStrapiRest &
  (
    | ResponsiveImageStrapiRest
    | TextBlockStrapiRest
    | IntroTextStrapiRest
    | VideoWithCaptionStrapiRest
  ))[];

export type ClaimWithContentPreview = {
  strapi_component: typeof componentName;
  Content: {
    ClaimPrefix: string | null;
    Claim: string;
    Title: string;
    ShowFinalDivider: boolean;
    Content: ClaimContent;
  };
};

export const isClaimWithContent = (
  component: PossibleContentSections,
): component is ClaimWithContentData =>
  component.strapi_component === componentName;

export const StrapiClaimWithContent = ({
  content,
  conversion,
  sapForm,
  ...context
}: ComponentProps<ClaimWithContentData>) => {
  if (content === null) {
    return null;
  }

  return (
    <ClaimWithContent
      claimPrefix={content.claimPrefix}
      claim={content.claim}
      claimOffsetForIntroText={
        content.content[0] && isIntroText(content.content[0])
      }
      title={content.title}
      showFinalDivider={content.showFinalDivider ?? undefined}
      sections={
        <Sections
          content={content.content}
          conversion={conversion}
          {...context}
        />
      }
    />
  );
};

export const config = {
  isComponent: isClaimWithContent,
  Component: StrapiClaimWithContent,
  componentName,
};

export const populationParams = {
  Content: {
    populate: {
      Content: {
        populate: {
          ...imagePopulate.populate,
          Video: {
            populate: {
              ...videoPopulate,
            },
          },
        },
      },
    },
  },
};

const mapClaimWithContent = (
  section: PossiblePreviewContentSections,
): ClaimWithContentData | null =>
  section.strapi_component === componentName
    ? ({
        ...section,
        content: section.Content && {
          claimPrefix: section.Content.ClaimPrefix,
          claim: section.Content.Claim,
          title: section.Content.Title,
          showFinalDivider: section.Content.ShowFinalDivider,
          content: (section as ClaimWithContentPreview).Content.Content.map(
            (c) => {
              if (c.strapi_component === 'shared.responsive-image') {
                return c;
              } else if (c.strapi_component === 'shared.textblock') {
                return {
                  ...c,
                  Content: markdownToHTML((c as TextBlockStrapiRest).Content),
                };
              } else if (c.strapi_component === 'shared.video-with-caption') {
                return {
                  strapi_component: c.strapi_component,
                  video: {
                    videoDesktop: (c as VideoWithCaptionStrapiRest).Video
                      .VideoDesktop,
                    videoMobile: (c as VideoWithCaptionStrapiRest).Video
                      .VideoMobile,
                    posterImage: (c as VideoWithCaptionStrapiRest).Video
                      .PosterImage,
                    autoPlay: (c as VideoWithCaptionStrapiRest).Video.AutoPlay,
                  },
                  caption: (c as VideoWithCaptionStrapiRest).Caption,
                };
              } else if (c.strapi_component === 'shared.intro-text') {
                return {
                  ...c,
                  text: markdownToHTML((c as IntroTextStrapiRest).text),
                  showCTA: (c as IntroTextStrapiRest).showCTA,
                };
              } else {
                return null;
              }
            },
          ).filter((c) => c !== null),
        },
      } as ClaimWithContentData)
    : null;

export const previewConfig: ContentLivePreviewConfig = {
  populationParams,
  componentName,
  mapProperties: mapClaimWithContent,
};
